"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
const partnerList = [{
  id: 1,
  name: "Bruker",
  focus: "Spectroscopy Innovation",
  summary: "Precision molecular spectroscopy, FT-IR, and Raman instruments for pharmaceutical QC and research.",
  logoSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/bruker_logo.png",
  alt: "Bruker partner logo",
  tag: "Spectroscopy"
}, {
  id: 2,
  name: "VIAVI Solutions",
  focus: "Process Analytical Technology",
  summary: "MicroNIR near-infrared spectrometers engineered for real-time in-line and at-line process monitoring.",
  logoSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/viavi_solutions_logo.png",
  alt: "VIAVI Solutions partner logo",
  tag: "PAT & NIR"
}, {
  id: 3,
  name: "Rigaku",
  focus: "X-Ray & Handheld Analysis",
  summary: "Advanced X-ray diffraction, fluorescence, and handheld Raman analyzers for raw material ID verification.",
  logoSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/rigaku_logo.png",
  alt: "Rigaku partner logo",
  tag: "X-Ray & Handheld"
}, {
  id: 4,
  name: "Copley Scientific",
  focus: "Inhaler & Dosage Testing",
  summary: "World-leading testing instrumentation for metered-dose inhalers, dry powders, tablets, and semisolids.",
  logoSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/copley_scientific_logo.png",
  alt: "Copley Scientific partner logo",
  tag: "Dosage Testing"
}, {
  id: 5,
  name: "ReSolve",
  focus: "Through-Barrier Raman",
  summary: "Specialized handheld Raman systems designed to identify chemicals through select opaque containers and packaging, technology and material dependent.",
  logoSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/resolve_logo.webp",
  alt: "ReSolve partner logo",
  tag: "Through-Barrier"
}];
export default function Partners() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  return <section id="technologypartners" data-nav="light" className="bg-background py-16 md:py-24 border-b border-border/40">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-3 px-3 py-1 font-medium text-xs uppercase tracking-wider">
            Authorized Technology Partners
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
            World-Class Analytical Instrumentation
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            We partner with leading global instrumentation manufacturers to provide regulated pharmaceutical, biopharma, and industrial laboratories with qualified technology, qualification, and lifelong service support.
          </p>
        </div>

        {/* 5-Card Grid */}
        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }}>
          {partnerList.map((partner, index) => <motion.div key={partner.id} data-index={index} variants={itemVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border/80 shadow-xs hover:shadow-md transition-shadow duration-200 bg-card">
                <CardHeader className="space-y-4 pb-4">
                  {/* Styled Logo Cradle / Frame */}
                  <div className="w-full h-24 rounded-lg bg-secondary/50 border border-border/60 flex items-center justify-center p-4">
                    <img src={partner.logoSrc} alt={partner.alt} className="h-7 w-auto max-w-[160px] object-contain filter grayscale hover:grayscale-0 transition-[filter] duration-200" loading="lazy" />
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <CardTitle className="text-xl font-bold text-foreground">
                        {partner.name}
                      </CardTitle>
                      <Badge variant="outline" className="text-[11px] font-medium border-border text-muted-foreground">
                        {partner.tag}
                      </Badge>
                    </div>
                    <p className="text-xs font-semibold text-primary uppercase tracking-wide">
                      {partner.focus}
                    </p>
                  </div>
                </CardHeader>

                <CardContent className="py-0">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {partner.summary}
                  </p>
                </CardContent>

                <CardFooter className="pt-6">
                  <Button variant="default" size="sm" className="w-full justify-between group" asChild>
                    <Link to="/ProductsPage">
                      <span>View Partner Profile</span>
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Assurance Note */}
        <div className="mt-12 p-4 md:p-6 rounded-lg bg-secondary/30 border border-border/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="size-5 text-primary shrink-0" />
            <p className="text-sm text-foreground">
              All technology partners backed by factory-trained qualification, IQ/OQ protocols, and regional preventative maintenance.
            </p>
          </div>
          <Button variant="outline" size="sm" asChild className="shrink-0">
            <Link to="/RequestAQuotePage">
              Request Technical Datasheet
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}